import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Wallet,
  Receipt,
  TrendingDown,
  AlertTriangle,
  Search,
  Plus,
  Check,
  X,
  MoreHorizontal,
  Pencil,
} from "lucide-react";

import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/contexts/AuthContext";
import { useTenant } from "@/contexts/TenantContext";
import { api, formatApiError } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { DateRangeFilter, presetRange } from "@/components/reports/DateRangeFilter";
import { BranchFilter } from "@/components/reports/BranchFilter";
import { KpiCard, formatMxn, formatInt } from "@/components/reports/KpiCard";
import { ExportMenu } from "@/components/reports/ExportMenu";

const PAYMENT_METHODS = [
  { value: "cash", label: "Efectivo" },
  { value: "transfer", label: "Transferencia" },
  { value: "card", label: "Tarjeta" },
  { value: "other", label: "Otro" },
];

const STATUS_LABELS = {
  registered: { label: "Registrado", tone: "bg-slate-100 text-slate-700" },
  approved: { label: "Aprobado", tone: "bg-emerald-100 text-emerald-800" },
  cancelled: { label: "Cancelado", tone: "bg-red-100 text-red-800" },
};

const EMPTY_FORM = {
  branch_id: "",
  expense_date: "",
  amount: "",
  category_id: "",
  payment_method: "cash",
  description: "",
  supplier_name: "",
  reference: "",
  notes: "",
  service_order_id: "",
};

function todayMx() {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Mexico_City",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
}

export default function Gastos() {
  const { hasPermission } = useAuth();
  const { isSuperAdmin, activeTenantId, branches, activeBranch } = useTenant();

  const canCreate = hasPermission("expenses:create");
  const canEdit = hasPermission("expenses:edit");
  const canApprove = hasPermission("expenses:approve");
  const canCancel = hasPermission("expenses:cancel");

  const [range, setRange] = useState(() => ({ preset: "month", ...presetRange("month") }));
  const [branchId, setBranchId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("");
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const [cancelDialog, setCancelDialog] = useState(null);
  const [cancelReason, setCancelReason] = useState("");

  const tenantParam = isSuperAdmin && activeTenantId ? { tenant_id: activeTenantId } : {};
  const canQuery = !isSuperAdmin || !!activeTenantId;

  const params = useMemo(() => {
    const p = { ...tenantParam };
    if (range.date_from) p.date_from = range.date_from;
    if (range.date_to) p.date_to = range.date_to;
    if (branchId) p.branch_id = branchId;
    if (categoryId) p.category_id = categoryId;
    if (paymentMethod) p.payment_method = paymentMethod;
    if (status) p.status = status;
    if (search) p.search = search;
    return p;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [range, branchId, categoryId, paymentMethod, status, search, activeTenantId]);

  const fetchAll = async () => {
    if (!canQuery) {
      setItems([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const [expRes, catRes] = await Promise.all([
        api.get("/expenses", { params }),
        api.get("/expense-categories", { params: { ...tenantParam, include_inactive: true } }),
      ]);
      setItems(expRes.data);
      setCategories(catRes.data);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const t = setTimeout(fetchAll, 250);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  const kpis = useMemo(() => {
    let approved = 0;
    let registered = 0;
    let today = 0;
    const t = todayMx();
    const byCat = {};
    items.forEach((e) => {
      const amt = Number(e.amount || 0);
      if (e.status === "approved") {
        approved += amt;
        if (e.expense_date === t) today += amt;
        byCat[e.category_name || e.category_code || "—"] =
          (byCat[e.category_name || e.category_code || "—"] || 0) + amt;
      } else if (e.status === "registered") {
        registered += amt;
      }
    });
    return { approved, registered, today, byCat };
  }, [items]);

  const openCreate = () => {
    setEditing(null);
    setForm({
      ...EMPTY_FORM,
      branch_id: activeBranch?.id || branches?.[0]?.id || "",
      expense_date: todayMx(),
      category_id: categories.find((c) => c.active)?.id || "",
    });
    setDialogOpen(true);
  };

  const openEdit = (e) => {
    setEditing(e);
    setForm({
      branch_id: e.branch_id || "",
      expense_date: e.expense_date || todayMx(),
      amount: e.amount ?? "",
      category_id: e.category_id || "",
      payment_method: e.payment_method || "cash",
      description: e.description || "",
      supplier_name: e.supplier_name || "",
      reference: e.reference || "",
      notes: e.notes || "",
      service_order_id: e.service_order_id || "",
    });
    setDialogOpen(true);
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = {
        branch_id: form.branch_id,
        expense_date: form.expense_date,
        amount: Number(form.amount),
        category_id: form.category_id,
        payment_method: form.payment_method,
        description: form.description,
        supplier_name: form.supplier_name || null,
        reference: form.reference || null,
        notes: form.notes || null,
      };
      if (editing) {
        await api.put(`/expenses/${editing.id}`, payload);
        toast.success("Gasto actualizado");
      } else {
        const { data } = await api.post("/expenses", payload, { params: tenantParam });
        toast.success(data.status === "approved" ? "Gasto registrado y aprobado" : "Gasto registrado");
      }
      setDialogOpen(false);
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const approve = async (e) => {
    try {
      await api.post(`/expenses/${e.id}/approve`);
      toast.success("Gasto aprobado");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const doCancel = async () => {
    if (!cancelDialog) return;
    if (!cancelReason.trim()) {
      toast.error("Ingresa el motivo de cancelación");
      return;
    }
    try {
      await api.post(`/expenses/${cancelDialog.id}/cancel`, { reason: cancelReason.trim() });
      toast.success("Gasto cancelado");
      setCancelDialog(null);
      setCancelReason("");
      fetchAll();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const activeCats = categories.filter((c) => c.active);
  const topCats = Object.entries(kpis.byCat).sort((a, b) => b[1] - a[1]).slice(0, 4);

  return (
    <div className="space-y-6" data-testid="page-gastos">
      <PageHeader
        title="Gastos diarios"
        description="Control administrativo de egresos del taller. Solo los gastos aprobados impactan reportes y dashboard."
        actions={
          <div className="flex gap-2">
            <ExportMenu
              endpoint="/expenses"
              params={params}
              permission="expenses:export"
              filename="gastos"
              testId="export-gastos"
            />
            {canCreate && (
              <Button className="btn-tenant" onClick={openCreate} data-testid="btn-new-expense">
                <Plus className="h-4 w-4 mr-2" /> Nuevo gasto
              </Button>
            )}
          </div>
        }
      />

      {/* Filters */}
      <Card className="border-border">
        <CardContent className="p-4 flex flex-col lg:flex-row gap-4 lg:items-end lg:justify-between">
          <DateRangeFilter value={range} onChange={setRange} />
          <div className="flex flex-wrap items-end gap-3">
            <BranchFilter value={branchId} onChange={setBranchId} />
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Categoría
              </Label>
              <Select value={categoryId || "__all__"} onValueChange={(v) => setCategoryId(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9 w-52" data-testid="filter-category">
                  <SelectValue placeholder="Sucursal activa" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todas</SelectItem>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{!c.active ? " (inactiva)" : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Método
              </Label>
              <Select value={paymentMethod || "__all__"} onValueChange={(v) => setPaymentMethod(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9 w-36" data-testid="filter-method">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[11px] uppercase tracking-wider text-muted-foreground">
                Estatus
              </Label>
              <Select value={status || "__all__"} onValueChange={(v) => setStatus(v === "__all__" ? "" : v)}>
                <SelectTrigger className="h-9 w-36" data-testid="filter-status">
                  <SelectValue placeholder="Todos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">Todos</SelectItem>
                  <SelectItem value="registered">Registrado</SelectItem>
                  <SelectItem value="approved">Aprobado</SelectItem>
                  <SelectItem value="cancelled">Cancelado</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="gastos-kpis">
        <KpiCard label="Aprobados del período" value={formatMxn(kpis.approved)} icon={Receipt} loading={loading} testId="kpi-approved" />
        <KpiCard label="Aprobados hoy" value={formatMxn(kpis.today)} icon={Wallet} loading={loading} testId="kpi-today" />
        <KpiCard
          label="Pendientes de aprobar"
          value={formatMxn(kpis.registered)}
          hint={canApprove ? "Puedes aprobarlos aquí" : "Solo dueño/gerente aprueba"}
          icon={AlertTriangle}
          loading={loading}
          testId="kpi-registered"
        />
        <KpiCard label="Movimientos" value={formatInt(items.length)} icon={TrendingDown} loading={loading} testId="kpi-count" />
      </div>

      {/* Top categorías */}
      {topCats.length > 0 && (
        <Card className="border-border" data-testid="top-categories">
          <CardHeader className="border-b border-border py-3">
            <CardTitle className="tracking-tight text-sm">Top categorías (aprobado)</CardTitle>
          </CardHeader>
          <CardContent className="p-3 flex flex-wrap gap-2">
            {topCats.map(([name, amount]) => (
              <div key={name} className="border rounded-md px-3 py-1.5 text-xs bg-slate-50 border-slate-200">
                <span className="text-slate-600">{name}</span>
                <span className="ml-2 font-semibold">{formatMxn(amount)}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Table */}
      <Card className="border-border" data-testid="gastos-table">
        <CardHeader className="border-b border-border py-3 flex flex-row items-center justify-between">
          <div>
            <CardTitle className="tracking-tight text-base">Gastos</CardTitle>
            <CardDescription>{loading ? "Cargando…" : `${items.length} resultados`}</CardDescription>
          </div>
          <div className="relative w-72">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Descripción, proveedor, folio…"
              className="pl-9 h-9"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="search-expenses"
            />
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6 space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-10" />)}
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              title="Sin gastos"
              description="Registra el primer gasto del período con el botón Nuevo gasto."
              icon={Receipt}
            />
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Folio</TableHead>
                    <TableHead>Fecha</TableHead>
                    <TableHead>Categoría</TableHead>
                    <TableHead>Descripción</TableHead>
                    <TableHead>Sucursal</TableHead>
                    <TableHead>Método</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                    <TableHead>Estatus</TableHead>
                    <TableHead>Usuario</TableHead>
                    <TableHead className="text-right">Acciones</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.map((e) => {
                    const st = STATUS_LABELS[e.status] || STATUS_LABELS.registered;
                    return (
                      <TableRow
                        key={e.id}
                        className={e.status === "cancelled" ? "opacity-60" : ""}
                        data-testid={`expense-row-${e.id}`}
                      >
                        <TableCell className="font-mono text-xs">{e.number}</TableCell>
                        <TableCell className="text-xs">{e.expense_date}</TableCell>
                        <TableCell className="text-sm">{e.category_name}</TableCell>
                        <TableCell className="text-sm max-w-sm truncate" title={e.description}>
                          {e.description}
                          {e.supplier_name && (
                            <div className="text-[10px] text-muted-foreground">
                              {e.supplier_name}
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="text-sm">{e.branch_name || "—"}</TableCell>
                        <TableCell className="text-xs">
                          {PAYMENT_METHODS.find((m) => m.value === e.payment_method)?.label || e.payment_method}
                        </TableCell>
                        <TableCell className="text-right font-semibold">{formatMxn(e.amount)}</TableCell>
                        <TableCell>
                          <Badge className={st.tone}>{st.label}</Badge>
                        </TableCell>
                        <TableCell className="text-xs">{e.created_by_name || "—"}</TableCell>
                        <TableCell className="text-right">
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`expense-actions-${e.id}`}>
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {canEdit && e.status === "registered" && (
                                <DropdownMenuItem onClick={() => openEdit(e)} data-testid={`expense-edit-${e.id}`}>
                                  <Pencil className="h-4 w-4 mr-2" /> Editar
                                </DropdownMenuItem>
                              )}
                              {canApprove && e.status === "registered" && (
                                <DropdownMenuItem onClick={() => approve(e)} data-testid={`expense-approve-${e.id}`}>
                                  <Check className="h-4 w-4 mr-2" /> Aprobar
                                </DropdownMenuItem>
                              )}
                              {canCancel && e.status !== "cancelled" && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem
                                    onClick={() => setCancelDialog(e)}
                                    className="text-red-600"
                                    data-testid={`expense-cancel-${e.id}`}
                                  >
                                    <X className="h-4 w-4 mr-2" /> Cancelar
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="expense-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Editar gasto" : "Nuevo gasto"}</DialogTitle>
            <DialogDescription>
              {editing
                ? "Solo se pueden editar gastos en estatus registrado."
                : canApprove
                ? "Se registrará y aprobará automáticamente (tu rol lo permite)."
                : "El gasto quedará como registrado hasta que gerencia lo apruebe."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Fecha *</Label>
                <Input
                  type="date"
                  value={form.expense_date}
                  onChange={(e) => setForm({ ...form, expense_date: e.target.value })}
                  data-testid="exp-date"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Monto *</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="exp-amount"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Sucursal *</Label>
                <Select value={form.branch_id} onValueChange={(v) => setForm({ ...form, branch_id: v })}>
                  <SelectTrigger data-testid="exp-branch"><SelectValue placeholder="Selecciona sucursal" /></SelectTrigger>
                  <SelectContent>
                    {branches.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Categoría *</Label>
                <Select value={form.category_id} onValueChange={(v) => setForm({ ...form, category_id: v })}>
                  <SelectTrigger data-testid="exp-category"><SelectValue placeholder="Selecciona categoría" /></SelectTrigger>
                  <SelectContent>
                    {activeCats.map((c) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Método de pago *</Label>
              <Select value={form.payment_method} onValueChange={(v) => setForm({ ...form, payment_method: v })}>
                <SelectTrigger data-testid="exp-method"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Descripción *</Label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                data-testid="exp-description"
                placeholder="¿En qué se gastó?"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Proveedor (opcional)</Label>
                <Input
                  value={form.supplier_name}
                  onChange={(e) => setForm({ ...form, supplier_name: e.target.value })}
                  data-testid="exp-supplier"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Referencia (opcional)</Label>
                <Input
                  value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  placeholder="Folio ticket, factura…"
                  data-testid="exp-reference"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Notas (opcional)</Label>
              <Textarea
                rows={2}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                data-testid="exp-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button
              className="btn-tenant"
              onClick={submit}
              disabled={saving || !form.branch_id || !form.category_id || !form.description || !form.amount}
              data-testid="exp-save"
            >
              {saving ? "Guardando…" : editing ? "Guardar" : "Registrar gasto"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Dialog */}
      <Dialog open={!!cancelDialog} onOpenChange={(o) => { if (!o) { setCancelDialog(null); setCancelReason(""); } }}>
        <DialogContent data-testid="cancel-dialog">
          <DialogHeader>
            <DialogTitle>Cancelar gasto {cancelDialog?.number}</DialogTitle>
            <DialogDescription>
              El gasto no se borra — queda con estatus <b>Cancelado</b> y trazabilidad completa.
              Es obligatorio capturar el motivo.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label>Motivo *</Label>
            <Textarea
              rows={3}
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              placeholder="Ejemplo: duplicado, capturado con error…"
              data-testid="cancel-reason"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setCancelDialog(null); setCancelReason(""); }}>Volver</Button>
            <Button variant="destructive" onClick={doCancel} data-testid="cancel-confirm">
              Cancelar gasto
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
